import { useEffect, useState } from "react";
import { api, formatApiError, formatIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Plus, Wrench, Disc3, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  ban: "Ban",
  sparepart: "Sparepart",
};
const CATEGORY_STYLE = {
  ban: "bg-slate-900 text-orange-400",
  sparepart: "bg-emerald-100 text-emerald-800 border border-emerald-300",
};

export default function VehicleExpenses({ vehicleType, vehicleId, currentKm }) {
  const { user } = useAuth();
  const isKoor = user?.role === "koordinator";
  const canDelete = user?.role === "management" || user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = {
    catalog_id: "",
    quantity: 1,
    event_date: new Date().toISOString().slice(0, 10),
    km: vehicleType === "truck" ? currentKm || 0 : "",
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/expenses`, { params: { vehicle_type: vehicleType, vehicle_id: vehicleId } })
      .then((r) => setRows(r.data))
      .catch(() => {});
  };
  const loadInv = () => api.get("/sparepart-inventory").then((r) => setInventory(r.data)).catch(() => {});
  useEffect(() => { load(); loadInv(); }, [vehicleType, vehicleId]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, km: vehicleType === "truck" ? currentKm || 0 : "" });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      catalog_id: r.sparepart_catalog_id || "",
      quantity: r.quantity ?? 1,
      event_date: r.event_date,
      km: r.km ?? "",
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!editing) {
        if (!form.catalog_id) { toast.error("Pilih sparepart dari inventory"); setBusy(false); return; }
        const payload = {
          vehicle_type: vehicleType,
          vehicle_id: vehicleId,
          category: "sparepart",
          sparepart_catalog_id: form.catalog_id,
          quantity: Number(form.quantity),
          event_date: form.event_date,
          notes: form.notes,
        };
        if (vehicleType === "truck" && form.km !== "") payload.km = Number(form.km);
        await api.post("/expenses", payload);
        toast.success("Sparepart tercatat, stok berkurang");
      } else {
        // Only allow editing event_date, km, notes (not qty since that affects stock)
        const payload = { event_date: form.event_date, notes: form.notes };
        if (vehicleType === "truck" && form.km !== "") payload.km = Number(form.km);
        await api.patch(`/expenses/${editing.id}`, payload);
        toast.success("Data diperbarui");
      }
      setOpen(false);
      setEditing(null);
      load();
      loadInv();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const selectedCat = inventory.find((i) => i.catalog_id === form.catalog_id);

  const del = async (r) => {
    if (!confirm(`Hapus entry ${r.description}?`)) return;
    try {
      await api.delete(`/expenses/${r.id}`);
      toast.success("Entry dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totalBan = rows.filter((r) => r.category === "ban").reduce((a, b) => a + (b.amount || 0), 0);
  const totalSpr = rows.filter((r) => r.category === "sparepart").reduce((a, b) => a + (b.amount || 0), 0);

  return (
    <div className="mt-8" data-testid="vehicle-expenses">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-black text-slate-900">Riwayat Biaya</h2>
          <p className="text-slate-500 text-sm mt-1">
            Pemakaian ban dan sparepart untuk kendaraan ini. {isKoor && "(harga hanya untuk management)"}
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial h-11"
          data-testid="add-sparepart-btn"
        >
          <Plus className="w-5 h-5 mr-1" /> Tambah Sparepart
        </Button>
      </div>

      {!isKoor && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <SummaryCard label="Total Biaya Ban" value={formatIDR(totalBan)} accent="bg-slate-900 text-orange-400" testId="expense-total-ban" />
          <SummaryCard label="Total Sparepart" value={formatIDR(totalSpr)} accent="bg-emerald-600 text-white" testId="expense-total-sparepart" />
          <SummaryCard label="Grand Total" value={formatIDR(totalBan + totalSpr)} accent="bg-orange-500 text-slate-900" testId="expense-grand-total" />
        </div>
      )}

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-900 hover:bg-slate-900">
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Tanggal</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Kategori</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Deskripsi</TableHead>
              {vehicleType === "truck" && <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">KM</TableHead>}
              {!isKoor && <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold text-right">Biaya</TableHead>}
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold w-24 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                <TableCell className="font-mono text-sm">{r.event_date}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${CATEGORY_STYLE[r.category]}`}>
                    {r.category === "ban" ? <Disc3 className="w-3 h-3 inline mr-1" /> : <Wrench className="w-3 h-3 inline mr-1" />}
                    {CATEGORY_LABEL[r.category]}
                  </span>
                </TableCell>
                <TableCell className="text-sm">
                  <div className="font-semibold">{r.description}</div>
                  {r.notes && <div className="text-xs text-slate-500">{r.notes}</div>}
                </TableCell>
                {vehicleType === "truck" && <TableCell className="font-mono text-sm">{r.km ? `${r.km.toLocaleString()} km` : "-"}</TableCell>}
                {!isKoor && <TableCell className="font-mono text-right">{formatIDR(r.amount)}</TableCell>}
                <TableCell className="text-right">
                  {r.source !== "install" && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-expense-${r.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => del(r)} data-testid={`delete-expense-${r.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={vehicleType === "truck" ? (isKoor ? 5 : 6) : (isKoor ? 4 : 5)} className="text-center py-8 text-slate-500 text-sm">
                  Belum ada catatan biaya. Klik <b>Tambah Sparepart</b> atau pasang ban untuk mulai.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">
              {editing ? "Edit Biaya" : "Tambah Sparepart"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {!editing ? (
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Pilih Sparepart dari Stok *</Label>
                <Select value={form.catalog_id} onValueChange={(v) => setForm({ ...form, catalog_id: v })}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="sparepart-catalog">
                    <SelectValue placeholder="-- Pilih sparepart --" />
                  </SelectTrigger>
                  <SelectContent>
                    {inventory.filter((i) => i.on_hand > 0).length === 0 ? (
                      <div className="p-3 text-sm text-slate-500">Belum ada sparepart di stok. Terima barang dulu di menu Stok Sparepart.</div>
                    ) : (
                      inventory.filter((i) => i.on_hand > 0).map((i) => (
                        <SelectItem key={i.catalog_id} value={i.catalog_id}>
                          {i.name} — stok {i.on_hand} {i.unit}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {selectedCat && (
                  <p className="text-xs text-slate-500 mt-1">
                    Tersedia: <span className="font-mono font-bold">{selectedCat.on_hand} {selectedCat.unit}</span>
                    {!isKoor && ` @ ${selectedCat.default_price ? new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(selectedCat.default_price) : "-"} / ${selectedCat.unit}`}
                  </p>
                )}
              </div>
            ) : (
              <div className="border-2 border-slate-900 p-3 rounded-sm bg-slate-50">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Sparepart</div>
                <div className="font-bold text-slate-900">{editing.description}</div>
                <p className="text-xs text-slate-500 mt-1">Jumlah pemakaian tidak dapat diubah setelah tercatat (agar stok konsisten).</p>
              </div>
            )}
            {!editing && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Jumlah {selectedCat && `(${selectedCat.unit})`} *</Label>
                  <Input
                    data-testid="sparepart-quantity"
                    required
                    type="number"
                    min={0}
                    step="0.1"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className="h-12 rounded-sm font-mono text-lg"
                    disabled={!form.catalog_id}
                  />
                </div>
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Tanggal *</Label>
                  <Input
                    data-testid="sparepart-date"
                    required
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    className="h-12 rounded-sm font-mono"
                  />
                </div>
              </div>
            )}
            {editing && (
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Tanggal *</Label>
                <Input required type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="h-12 rounded-sm font-mono" data-testid="sparepart-date" />
              </div>
            )}
            {vehicleType === "truck" && (
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">KM (opsional)</Label>
                <Input
                  data-testid="sparepart-km"
                  type="number"
                  min={0}
                  value={form.km}
                  onChange={(e) => setForm({ ...form, km: e.target.value })}
                  className="h-12 rounded-sm font-mono"
                />
              </div>
            )}
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">Catatan (opsional)</Label>
              <Input
                data-testid="sparepart-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="h-12 rounded-sm"
                placeholder="No invoice, bengkel, dll"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
              <Button type="submit" disabled={busy || (!editing && !form.catalog_id)} data-testid="sparepart-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                {busy ? "..." : editing ? "Simpan" : "Tambah"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, accent, testId }) {
  return (
    <div className="border-2 border-slate-200 bg-white p-3 rounded-sm" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className={`inline-block px-2 py-1 mt-1 rounded-sm font-mono font-bold text-sm ${accent}`}>{value}</div>
    </div>
  );
}
